/**
 * ISATS Portal — PII display helpers.
 *
 * Application PII fields are stored as { iv, ciphertext, authTag }
 * AES-256-GCM blocks and are decrypted ON THE SERVER before payloads
 * reach the client (P0-1a — the client no longer holds the DEK;
 * GET /api/pii-key was deleted). These helpers remain as the render
 * guard: any value that still arrives as an encrypted block (a code
 * path that missed server-side decryption) renders the fallback
 * instead of throwing React error #31 ("Objects are not valid as a
 * React child").
 */
import React, { useEffect, useState } from 'react';
import { isEncryptedPiiBlock, stripEnvelopesDeep } from './piiEnvelope';

export type { EncryptedPiiBlock } from './piiEnvelope';
export { isEncryptedPiiBlock } from './piiEnvelope';
export {
  PII_DISPLAY_PLACEHOLDER,
  sanitizeEnvelopesDeep,
  sanitizeTemplateRecord,
  searchText,
} from './piiEnvelope';

/**
 * Synchronous display guard for a single value that may be a PII field:
 * - string → returned as-is
 * - number / boolean → stringified
 * - encrypted block, any other object, array, null/undefined → fallback
 * Never returns a non-string, so a value passed through this helper can
 * always be rendered as a React child (no React error #31) and can
 * always receive string operations (no TypeError from .indexOf/.trim
 * on an envelope). Use at render sites that cannot use <PiiText />.
 */
export function safePiiText(value: any, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

/**
 * Deep display sanitizer: returns a copy of the value with every
 * encrypted PII block replaced by `undefined`, so downstream renders
 * fall back to their normal empty states ('—', 'not provided', …)
 * instead of rendering the envelope object (React error #31).
 *
 * - Only plain objects and arrays are walked; class instances (Date,
 *   Firestore Timestamp, …) pass through untouched.
 * - Returns the SAME reference when no block is found, so memoized
 *   consumers do not re-render on unchanged data.
 * - Depth-capped; anything deeper is returned as-is (Firestore
 *   payloads are shallow JSON and never approach the cap).
 *
 * Implemented by the shared walker in src/lib/piiEnvelope.ts (the
 * `depth` parameter is kept for call compatibility; the walk always
 * starts at the top of the given value).
 */
export function sanitizeEncryptedPiiBlocks<T>(value: T, depth = 0): T {
  void depth;
  return stripEnvelopesDeep(value);
}

/**
 * Resolve any value to a displayable string:
 * - string → returned as-is
 * - { iv, ciphertext, authTag } → fallback (decryption is server-side
 *   since P0-1a; a block reaching the client is never rendered raw)
 * - null/undefined/anything else → fallback ('—')
 * Never throws; never returns a non-string.
 */
export async function decryptPiiValue(value: any, fallback = '—'): Promise<string> {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

interface PiiTextProps {
  value: any;
  fallback?: string;
  className?: string;
}

/**
 * Renders a PII value that may be plaintext or an encrypted block.
 * Shows a subtle placeholder while decrypting; never renders a raw object.
 */
export function PiiText({ value, fallback = '—', className }: PiiTextProps): React.ReactElement {
  const [text, setText] = useState<string | null>(
    typeof value === 'string' ? value : value === null || value === undefined ? fallback : null
  );

  useEffect(() => {
    let alive = true;
    if (typeof value === 'string' || value === null || value === undefined) return;
    decryptPiiValue(value, fallback).then((t) => {
      if (alive) setText(t);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (text === null) {
    return React.createElement('span', { className, 'aria-busy': true }, '…');
  }
  return React.createElement('span', { className }, text);
}
